import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { billingQueryKeys } from '@/entities/billing';
import { api } from '@/shared/api/base';
import { useAuthBootstrap } from '@/shared/lib';

import type {
  AuthMeResponse,
  CompanyInvitationItem,
  CompanyInviteCandidateItem,
  CompanyItem,
  CompanyListResponse,
  CompanyUserListResponse,
  CompanyWorkspaceItem,
  CreateCompanyRequest,
  UpdateCompanyRequest,
} from './types';

const keys = {
  root: () => ['companies'],
  list: () => [...keys.root(), 'list'],
  me: () => ['protocolist-me'],
  detail: (companyId: string) => [...keys.root(), companyId, 'detail'] as const,
  users: (companyId: string) => [...keys.root(), companyId, 'users'],
  workspaces: (companyId: string, providerId: string) =>
    [...keys.root(), companyId, 'workspaces', providerId] as const,
  invitations: (companyId: string) => [...keys.root(), companyId, 'invitations'] as const,
  inviteCandidates: (companyId: string, query: string) =>
    [...keys.root(), companyId, 'invite-candidates', query] as const,
};

/** Список компаний текущего пользователя */
export async function listCompanies(): Promise<CompanyListResponse> {
  const { data } = await api.instance.get<CompanyListResponse>('/companies');
  return data ?? { items: [] };
}

/** Создать компанию (создатель становится owner) */
export async function createCompany(payload: CreateCompanyRequest): Promise<CompanyItem> {
  const { data } = await api.instance.post<CompanyItem>('/companies', payload);
  if (!data) throw new Error('Create company failed');
  return data;
}

/** Обновить компанию (название) */
export async function updateCompany(
  companyId: string,
  payload: UpdateCompanyRequest,
): Promise<CompanyItem> {
  const { data } = await api.instance.patch<CompanyItem>(`/companies/${companyId}`, payload);
  if (!data) throw new Error('Update company failed');
  return data;
}

/** GET /auth/me — id пользователя и feature flags */
export async function getAuthMe(): Promise<AuthMeResponse> {
  const { data } = await api.instance.get<AuthMeResponse>('/auth/me');
  if (!data) throw new Error('Auth me failed');
  return data;
}

/** Обновить display_name пользователя */
export async function updateDisplayName(displayName: string): Promise<{ display_name: string }> {
  const { data } = await api.instance.patch<{ display_name: string }>('/users/me/display-name', {
    display_name: displayName,
  });
  if (!data) throw new Error('Update display name failed');
  return data;
}

/** Список участников компании (owner/admin компании) */
export async function listCompanyUsers(companyId: string): Promise<CompanyUserListResponse> {
  const { data } = await api.instance.get<CompanyUserListResponse>(`/companies/${companyId}/users`);
  return data ?? { items: [] };
}

/** Изменить роль участника компании (owner/admin компании; owner назначается только при создании компании) */
export async function updateCompanyUserRole(
  companyId: string,
  userId: string,
  role: string,
): Promise<void> {
  await api.instance.patch(`/companies/${companyId}/users/${userId}/role`, { role });
}

/** Список воркспейсов компании по провайдеру (owner/admin) */
export async function listCompanyWorkspaces(
  companyId: string,
  providerId: string,
): Promise<CompanyWorkspaceItem[]> {
  const { data } = await api.instance.get<CompanyWorkspaceItem[]>(
    `/companies/${companyId}/providers/${providerId}/workspaces`,
  );
  return Array.isArray(data) ? data : [];
}

/** Обновить воркспейс (группа встреч и/или display_name, owner/admin компании) */
export async function updateWorkspaceMeeting(
  companyId: string,
  workspaceId: string,
  payload: { default_meeting_group_id?: string | null; display_name?: string | null },
): Promise<void> {
  await api.instance.patch(`/workspaces/${workspaceId}`, payload, {
    params: { company_id: companyId },
  });
}

/** Удалить участника компании (owner/admin, права контролирует бэкенд) */
export async function removeCompanyUser(companyId: string, userId: string): Promise<void> {
  await api.instance.delete(`/companies/${companyId}/users/${userId}`);
}

/** Удалить компанию (только владелец, контролирует бэкенд) */
export async function deleteCompany(companyId: string): Promise<void> {
  await api.instance.delete(`/companies/${companyId}`);
}

/** Список исходящих приглашений компании (owner/admin) */
export async function listCompanyInvitations(
  companyId: string,
): Promise<{ items: CompanyInvitationItem[] }> {
  const { data } = await api.instance.get<{ items: CompanyInvitationItem[] }>(
    `/companies/${companyId}/invitations`,
  );
  return data ?? { items: [] };
}

/** Список кандидатов для приглашения в компанию по display_name (только owner, при включённом feature flag) */
export async function listCompanyInviteCandidates(
  companyId: string,
  query: string,
): Promise<{ items: CompanyInviteCandidateItem[] }> {
  const { data } = await api.instance.get<{ items: CompanyInviteCandidateItem[] }>(
    `/companies/${companyId}/invite-candidates`,
    { params: { q: query } },
  );
  return data ?? { items: [] };
}

/** Создать приглашение в компанию (только owner) */
export async function createCompanyInvitation(
  companyId: string,
  invitee_id_or_external: string,
): Promise<CompanyInvitationItem> {
  const { data } = await api.instance.post<CompanyInvitationItem>(
    `/companies/${companyId}/invitations`,
    { invitee_id_or_external },
  );
  if (!data) throw new Error('Create invitation failed');
  return data;
}

export const useCompaniesQuery = () => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: keys.list(),
    queryFn: listCompanies,
    enabled: hasToken,
    staleTime: 5 * 60_000, // 5 minutes
  });
};

export const useAuthMeQuery = () => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: keys.me(),
    queryFn: getAuthMe,
    enabled: hasToken,
    staleTime: 30_000, // 30 seconds
    placeholderData: (previousData: any) => previousData,
  });
};

export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCompany,
    onSuccess: (createdCompany) => {
      queryClient.setQueryData<CompanyListResponse | undefined>(keys.list(), (current) => {
        const items = current?.items ?? [];
        if (items.some((company) => company.id === createdCompany.id)) {
          return current ?? { items };
        }
        return { items: [...items, createdCompany] };
      });
      queryClient.invalidateQueries({ queryKey: keys.list() });
      queryClient.invalidateQueries({ queryKey: keys.me() });
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.root() });
    },
  });
};

export const useUpdateCompanyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, name }: { companyId: string; name: string }) =>
      updateCompany(companyId, { name }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.list() });
      if (variables.companyId) {
        queryClient.invalidateQueries({ queryKey: keys.detail(variables.companyId) });
        queryClient.invalidateQueries({ queryKey: keys.users(variables.companyId) });
        queryClient.invalidateQueries({ queryKey: keys.workspaces(variables.companyId, 'meet') });
      }
    },
  });
};

export const useCompanyUsersQuery = (companyId: string | undefined) => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: keys.users(companyId ?? ''),
    queryFn: () => listCompanyUsers(companyId!),
    enabled: hasToken && !!companyId,
    staleTime: 30_000,
  });
};

export const useUpdateCompanyUserRoleMutation = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      updateCompanyUserRole(companyId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.users(companyId) });
    },
  });
};

export const useCompanyWorkspacesQuery = (companyId: string | undefined, providerId: string) => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: keys.workspaces(companyId ?? '', providerId),
    queryFn: () => listCompanyWorkspaces(companyId!, providerId),
    enabled: hasToken && !!companyId,
    staleTime: 30_000,
  });
};

export const useUpdateWorkspaceMeetingGroupMutation = (
  companyId?: string,
  providerId: string = 'meet',
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      default_meeting_group_id,
      display_name,
    }: {
      workspaceId: string;
      default_meeting_group_id?: string | null;
      display_name?: string | null;
    }) => {
      if (!companyId) {
        throw new Error('companyId is required to update workspace');
      }
      return updateWorkspaceMeeting(companyId, workspaceId, {
        default_meeting_group_id,
        display_name,
      });
    },
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: keys.workspaces(companyId, providerId) });
      }
      queryClient.invalidateQueries({ queryKey: keys.list() });
    },
  });
};

export const useDeleteWorkspaceFromCompanyMutation = (
  companyId: string | undefined,
  providerId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      api.workspaces.deleteWorkspaceWorkspacesWorkspaceIdDelete(workspaceId, {
        params: { company_id: companyId ?? undefined },
      }),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: keys.workspaces(companyId, providerId) });
      }
      queryClient.invalidateQueries({ queryKey: keys.list() });
    },
  });
};

export const useRemoveCompanyUserMutation = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => {
      if (!companyId) {
        throw new Error('companyId is required to remove company user');
      }
      return removeCompanyUser(companyId, userId);
    },
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: keys.users(companyId) });
        queryClient.invalidateQueries({ queryKey: keys.list() });
      }
    },
  });
};

export const useDeleteCompanyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => deleteCompany(companyId),
    onSuccess: (_data, companyId) => {
      queryClient.invalidateQueries({ queryKey: keys.list() });
      queryClient.invalidateQueries({ queryKey: keys.me() });
      queryClient.invalidateQueries({ queryKey: billingQueryKeys.root() });

      if (companyId) {
        // Очищаем кэш деталей и связанных сущностей удалённой компании
        queryClient.removeQueries({ queryKey: keys.detail(companyId) });
        queryClient.removeQueries({ queryKey: keys.users(companyId) });
        queryClient.removeQueries({ queryKey: keys.invitations(companyId) });
        queryClient.removeQueries({ queryKey: keys.workspaces(companyId, 'meet') });
      }
    },
  });
};

export const useCompanyInvitationsQuery = (companyId: string | undefined) => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: keys.invitations(companyId ?? ''),
    queryFn: () => listCompanyInvitations(companyId!),
    enabled: hasToken && !!companyId,
    staleTime: 30_000,
  });
};

export const useCreateCompanyInvitationMutation = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitee_id_or_external: string) =>
      createCompanyInvitation(companyId, invitee_id_or_external),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.invitations(companyId) });
      queryClient.invalidateQueries({
        queryKey: [...keys.root(), companyId, 'invite-candidates'],
      });
    },
  });
};

export const useCompanyInviteCandidatesQuery = (
  companyId: string | undefined,
  query: string,
  enabled: boolean,
) => {
  const { hasToken } = useAuthBootstrap();
  return useQuery({
    queryKey: keys.inviteCandidates(companyId ?? '', query),
    queryFn: () => listCompanyInviteCandidates(companyId!, query),
    enabled: hasToken && !!companyId && enabled,
    staleTime: 30_000,
    retry: false,
  });
};

export const useUpdateDisplayNameMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDisplayName,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.me() });
    },
  });
};
